import { supabase } from '../lib/supabase';

/**
 * Fetch dashboard financial metrics
 * Returns:
 * - totalRevenue: Total revenue from negotiations (sum of sale_value)
 * - totalPaidToSuppliers: Total paid to suppliers (sum of supplier_cost from negotiations)
 * - netProfit: Total net profit (sum of sale_value - supplier_cost)
 * - pendingPayables: Total pending payables (status = 'pending')
 * - pendingReceivables: Total pending receivables (status = 'pending')
 * - overduePayables: Count of overdue payables
 * - overdueReceivables: Count of overdue receivables
 */
export const getDashboardMetrics = async () => {
  try {
    // Fetch all negotiations to calculate revenue, supplier costs, and profit
    const { data: negotiations, error: negotiationsError } = await supabase?.from('negotiations')?.select('sale_value, supplier_cost');

    if (negotiationsError) throw negotiationsError;

    // Calculate total revenue (sum of all sale_value)
    const totalRevenue = negotiations?.reduce((sum, item) => sum + parseFloat(item?.sale_value || 0), 0) || 0;

    // 🔥 FIX: Calculate total paid to suppliers = sum of supplier_cost from negotiations
    const totalPaidToSuppliers = negotiations?.reduce((sum, item) => sum + parseFloat(item?.supplier_cost || 0), 0) || 0;

    // Calculate net profit (sum of sale_value - supplier_cost)
    const netProfit = negotiations?.reduce((sum, item) => {
      const saleValue = parseFloat(item?.sale_value || 0);
      const supplierCost = parseFloat(item?.supplier_cost || 0);
      return sum + (saleValue - supplierCost);
    }, 0) || 0;

    // Fetch pending payables
    const { data: payables, error: payablesError } = await supabase?.from('payables')?.select('amount, due_date')?.eq('status', 'pending');

    if (payablesError) throw payablesError;

    const totalPendingPayables = payables?.reduce((sum, item) => sum + parseFloat(item?.amount || 0), 0) || 0;
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const overduePayables = payables?.filter(item => item?.due_date < today)?.length || 0;

    // 🔥 FIX: Fetch ONLY pending receivables amounts (already correct, just confirming)
    const { data: receivables, error: receivablesError } = await supabase?.from('receivables')?.select('amount, status, due_date')?.in('status', ['pending', 'partial']);

    if (receivablesError) throw receivablesError;

    // Sum only pending receivables amounts (not total negotiation values)
    const totalPendingReceivables = receivables?.reduce((sum, r) => sum + parseFloat(r?.amount || 0), 0) || 0;
    const overdueReceivables = receivables?.filter(item => item?.due_date < today)?.length || 0;

    return {
      totalRevenue,
      totalPaidToSuppliers,
      netProfit,
      pendingPayables: totalPendingPayables,
      pendingReceivables: totalPendingReceivables,
      overduePayables,
      overdueReceivables,
      // Legacy compatibility
      totalIncome: totalRevenue,
      totalExpenses: totalPaidToSuppliers,
      netCashFlow: netProfit
    };
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    throw error;
  }
};

/**
 * Fetch recent transactions for dashboard
 * @param {number} limit - Number of recent transactions to fetch
 */
export const getRecentTransactions = async (limit = 10) => {
  try {
    const { data, error } = await supabase?.from('bank_transactions')?.select('*')?.order('transaction_date', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(transaction => ({
      id: transaction?.id,
      description: transaction?.description,
      amount: parseFloat(transaction?.amount),
      date: transaction?.transaction_date,
      type: transaction?.transaction_type,
      bankAccount: transaction?.bank_account
    })) || [];
  } catch (error) {
    console.error('Error fetching recent transactions:', error?.message);
    throw error;
  }
};

/**
 * Fetch upcoming payables (due in next 30 days)
 */
export const getUpcomingPayables = async () => {
  try {
    const today = new Date();
    const futureDate = new Date();
    futureDate?.setDate(today?.getDate() + 30);

    const { data, error } = await supabase?.from('payables')?.select(`
        *,
        suppliers (name),
        negotiations (title)
      `)?.eq('status', 'pending')?.gte('due_date', today?.toISOString()?.split('T')?.[0])?.lte('due_date', futureDate?.toISOString()?.split('T')?.[0])?.order('due_date', { ascending: true });

    if (error) throw error;

    return data?.map(payable => ({
      id: payable?.id,
      amount: parseFloat(payable?.amount),
      dueDate: payable?.due_date,
      supplierName: payable?.suppliers?.name,
      negotiationTitle: payable?.negotiations?.title,
      paymentMethod: payable?.payment_method
    })) || [];
  } catch (error) {
    console.error('Error fetching upcoming payables:', error?.message);
    throw error;
  }
};

/**
 * Fetch upcoming receivables (due in next 30 days)
 */
export const getUpcomingReceivables = async () => {
  try {
    const today = new Date();
    const futureDate = new Date();
    futureDate?.setDate(today?.getDate() + 30);

    const { data, error } = await supabase?.from('receivables')?.select(`
        *,
        negotiations (
          title,
          clients (name)
        )
      `)?.eq('status', 'pending')?.gte('due_date', today?.toISOString()?.split('T')?.[0])?.lte('due_date', futureDate?.toISOString()?.split('T')?.[0])?.order('due_date', { ascending: true });

    if (error) throw error;

    return data?.map(receivable => ({
      id: receivable?.id,
      amount: parseFloat(receivable?.amount),
      dueDate: receivable?.due_date,
      clientName: receivable?.negotiations?.clients?.name,
      negotiationTitle: receivable?.negotiations?.title,
      paymentMethod: receivable?.payment_method
    })) || [];
  } catch (error) {
    console.error('Error fetching upcoming receivables:', error?.message);
    throw error;
  }
};

/**
 * Fetch actual cash flow data based on ALL receivables and expenses (not just paid)
 * Calculates running balance from a starting bank balance including forecasted transactions
 * @param {number} startingBalance - Initial bank balance
 * @param {string} startDate - Start date for cash flow calculation (format: YYYY-MM-DD)
 * @param {string} endDate - End date for cash flow calculation (format: YYYY-MM-DD)
 */
export const getActualCashFlowData = async (startingBalance, startDate, endDate) => {
  try {
    // 🔥 FIX: Use actual date parameters instead of hardcoded December dates
    // Fetch receivables: include if received_at OR due_date is in date range
    const { data: allReceivables, error: receivablesError } = await supabase
      ?.from('receivables')
      ?.select('amount, received_at, due_date, status')
      ?.in('status', ['paid', 'pending', 'partial', 'overdue'])
      ?.or(`and(received_at.gte.${startDate},received_at.lte.${endDate}),and(due_date.gte.${startDate},due_date.lte.${endDate})`)
      ?.order('due_date', { ascending: true });

    if (receivablesError) throw receivablesError;

    // Fetch expenses: filter by expense_date in date range
    const { data: allExpenses, error: expensesError } = await supabase
      ?.from('expense')
      ?.select('amount, expense_date, status')
      ?.in('status', ['paid', 'pending', 'overdue'])
      ?.gte('expense_date', startDate)
      ?.lte('expense_date', endDate)
      ?.order('expense_date', { ascending: true });

    if (expensesError) throw expensesError;

    // Create a map to aggregate transactions by date
    const cashFlowMap = new Map();
    
    // Initialize with starting balance
    let currentBalance = parseFloat(startingBalance);

    // Process receivables - use ACTUAL received date for paid items
    allReceivables?.forEach(receivable => {
      let date;
      
      // If paid and has received_at, use that date (actual payment date)
      if (receivable?.status === 'paid' && receivable?.received_at) {
        date = receivable?.received_at?.split('T')?.[0];
      } else {
        // For pending/partial/overdue, use due_date (forecasted date)
        date = receivable?.due_date;
      }
      
      // Only include if date is in range
      if (date >= startDate && date <= endDate) {
        if (!cashFlowMap?.has(date)) {
          cashFlowMap?.set(date, { income: 0, expenses: 0 });
        }
        const entry = cashFlowMap?.get(date);
        entry.income += parseFloat(receivable?.amount || 0);
      }
    });

    // Process expenses - always use expense_date
    allExpenses?.forEach(expense => {
      let date = expense?.expense_date;
      
      // Only include if date is in range
      if (date >= startDate && date <= endDate) {
        if (!cashFlowMap?.has(date)) {
          cashFlowMap?.set(date, { income: 0, expenses: 0 });
        }
        const entry = cashFlowMap?.get(date);
        entry.expenses += parseFloat(expense?.amount || 0);
      }
    });

    // Convert map to array and calculate running balance
    const sortedDates = Array.from(cashFlowMap?.keys())?.sort();
    const cashFlowData = sortedDates?.map(date => {
      const { income, expenses } = cashFlowMap?.get(date);
      currentBalance = currentBalance + income - expenses;
      
      return {
        date,
        income,
        expenses,
        actualBalance: currentBalance
      };
    });

    // If no transactions, return starting balance for start date
    if (cashFlowData?.length === 0) {
      return [{
        date: startDate,
        income: 0,
        expenses: 0,
        actualBalance: currentBalance
      }];
    }

    return cashFlowData;
  } catch (error) {
    console.error('Error fetching actual cash flow data:', error);
    throw error;
  }
};

/**
 * Fetch detailed transaction list for cash flow with running balance
 * Returns list of ALL receivables and expenses with descriptions and status indicators
 * @param {number} startingBalance - Initial bank balance
 * @param {string} startDate - Start date for transactions (format: YYYY-MM-DD)
 * @param {string} endDate - End date for transactions (format: YYYY-MM-DD)
 */
export const getCashFlowTransactionsList = async (startingBalance, startDate, endDate) => {
  try {
    // 🔥 FIX: Use actual date parameters instead of hardcoded December dates
    // Fetch receivables with negotiation and client details
    const { data: allReceivables, error: receivablesError } = await supabase
      ?.from('receivables')
      ?.select(`
        amount,
        received_at,
        due_date,
        status,
        negotiations (
          title,
          clients (name)
        )
      `)
      ?.in('status', ['paid', 'pending', 'partial', 'overdue'])
      ?.or(`and(received_at.gte.${startDate},received_at.lte.${endDate}),and(due_date.gte.${startDate},due_date.lte.${endDate})`)
      ?.order('due_date', { ascending: true });

    if (receivablesError) throw receivablesError;

    // Fetch expenses with category details
    const { data: allExpenses, error: expensesError } = await supabase
      ?.from('expense')
      ?.select(`
        amount,
        expense_date,
        description,
        category,
        status
      `)
      ?.in('status', ['paid', 'pending', 'overdue'])
      ?.gte('expense_date', startDate)
      ?.lte('expense_date', endDate)
      ?.order('expense_date', { ascending: true });

    if (expensesError) throw expensesError;

    // Combine and sort all transactions chronologically
    const allTransactions = [];
    
    // Add receivables as income
    allReceivables?.forEach(receivable => {
      let transactionDate;
      
      // For paid items, prefer received_at (actual date)
      if (receivable?.status === 'paid' && receivable?.received_at) {
        transactionDate = receivable?.received_at?.split('T')?.[0];
      } else {
        // For pending/partial/overdue, use due_date
        transactionDate = receivable?.due_date;
      }
      
      // Only include transactions in date range
      if (transactionDate >= startDate && transactionDate <= endDate) {
        allTransactions?.push({
          date: transactionDate,
          type: 'income',
          amount: parseFloat(receivable?.amount || 0),
          description: receivable?.negotiations?.title || 'Negociação',
          source: receivable?.negotiations?.clients?.name 
            ? `Cliente: ${receivable?.negotiations?.clients?.name}` 
            : 'Receita',
          status: receivable?.status,
          isPaid: receivable?.status === 'paid'
        });
      }
    });

    // Add expenses as outflow
    allExpenses?.forEach(expense => {
      let transactionDate = expense?.expense_date;
      
      // Only include transactions in date range
      if (transactionDate >= startDate && transactionDate <= endDate) {
        allTransactions?.push({
          date: transactionDate,
          type: 'expense',
          amount: parseFloat(expense?.amount || 0),
          description: expense?.description || 'Despesa',
          source: expense?.category || 'Despesa geral',
          status: expense?.status,
          isPaid: expense?.status === 'paid'
        });
      }
    });

    // Sort by date
    allTransactions?.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate running balance
    let currentBalance = parseFloat(startingBalance);
    const transactionsWithBalance = allTransactions?.map(transaction => {
      if (transaction?.type === 'income') {
        currentBalance += transaction?.amount;
      } else {
        currentBalance -= transaction?.amount;
      }

      return {
        ...transaction,
        balance: currentBalance
      };
    });

    return transactionsWithBalance;
  } catch (error) {
    console.error('Error fetching cash flow transactions list:', error);
    throw error;
  }
};

/**
 * Get comprehensive financial data for export
 * This aggregates all dashboard metrics and recent transactions for PDF/Excel export
 */
export const getExportFinancialData = async () => {
  try {
    // Get main dashboard metrics
    const metrics = await getDashboardMetrics();
    
    // Get recent transactions (last 30 transactions for exports)
    const recentTransactions = await getRecentTransactions(30);
    
    // Get upcoming payables and receivables
    const upcomingPayables = await getUpcomingPayables();
    const upcomingReceivables = await getUpcomingReceivables();
    
    // Get cash flow data for last 30 days
    const endDate = new Date()?.toISOString()?.split('T')?.[0];
    const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
    const cashFlowData = await getActualCashFlowData(metrics?.netProfit, startDate, endDate);
    
    return {
      ...metrics,
      recentTransactions,
      upcomingPayables,
      upcomingReceivables,
      cashFlowData,
      exportDate: new Date()?.toISOString(),
      reportPeriod: {
        start: startDate,
        end: endDate
      }
    };
  } catch (error) {
    console.error('Error fetching export financial data:', error);
    throw error;
  }
};